import { ReturnHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { HELP_CENTER_HOW_TO_CONFIGURE_SSO } from '@modules/workspaceSettings/constants';
import { SSOType } from '@modules/workspaceSettings/types';
import { HelpRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  alpha,
} from '@mui/material';

interface ConfigureSSOSelectionModalContentProps {
  onNext: () => void;
  selectedSSOConfig: SSOType;
  onChangeSSOConfig: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ConfigureSSOSelectionModalContent = ({
  onNext,
  selectedSSOConfig,
  onChangeSSOConfig,
}: ConfigureSSOSelectionModalContentProps) => {
  const { closeModal } = useMuiModalContext();

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        onNext();
      }}
    >
      <DialogContent dividers>
        <Typography variant="body2" mb={2}>
          Please select a configuration to proceed with the SSO setup{' '}
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <RadioGroup
            name="ssoType"
            value={selectedSSOConfig}
            onChange={onChangeSSOConfig}
            defaultValue={selectedSSOConfig}
          >
            <FormControlLabel
              value={SSOType.SAML}
              control={<Radio />}
              sx={{
                backgroundColor:
                  selectedSSOConfig === SSOType.SAML
                    ? (theme) => alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                borderRadius: 1,
                padding: 1,
              }}
              label={
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: (theme) =>
                        selectedSSOConfig === SSOType.SAML
                          ? theme.palette.primary.main
                          : 'inherit',
                    }}
                  >
                    SAML{' '}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Security Assertion Markup Language{' '}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              sx={{
                mt: 2,
                backgroundColor:
                  selectedSSOConfig === SSOType.OIDC
                    ? (theme) => alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                padding: 1,
                borderRadius: 1,
              }}
              value={SSOType.OIDC}
              control={<Radio />}
              label={
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: (theme) =>
                        selectedSSOConfig === SSOType.OIDC
                          ? theme.palette.primary.main
                          : 'inherit',
                    }}
                  >
                    OIDC{' '}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    OpenID Connect
                  </Typography>
                </>
              }
            />
          </RadioGroup>
        </Paper>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HelpRounded fontSize="small" color="disabled" />
          <Typography variant="caption">
            Need help in Configuring SSO?
          </Typography>
          <Link
            href={HELP_CENTER_HOW_TO_CONFIGURE_SSO}
            target="_blank"
            display="flex"
            alignItems="center"
          >
            <Typography variant="caption">See Documentation</Typography>
          </Link>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ m: 1, mr: 2 }}>
        <Button variant="outlined" onClick={() => closeModal()}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          endIcon={<ReturnHotKeyIcon isDisabled={false} />}
        >
          Proceed
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};
